import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface ColorPalette {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  explanation: string;
}

export interface BrandVoiceSuggestion {
  recommended: string;
  explanation: string;
}

interface SuggestPaletteResponse {
  recommended: number;
  palettes: ColorPalette[];
  brandVoice?: BrandVoiceSuggestion;
}

interface UseBrandingSuggestionResult {
  isLoading: boolean;
  error: string | null;
  palettes: ColorPalette[];
  recommendedIndex: number;
  brandVoice: BrandVoiceSuggestion | null;
  suggestPalette: (
    projectId: string,
    projectDescription: string,
    projectTitle?: string
  ) => Promise<void>;
  clearError: () => void;
}

/**
 * Hook for AI-powered brand palette suggestions
 */
export function useBrandingSuggestion(): UseBrandingSuggestionResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [palettes, setPalettes] = useState<ColorPalette[]>([]);
  const [recommendedIndex, setRecommendedIndex] = useState(0);
  const [brandVoice, setBrandVoice] = useState<BrandVoiceSuggestion | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const suggestPalette = useCallback(
    async (
      projectId: string,
      projectDescription: string,
      projectTitle?: string
    ): Promise<void> => {
      if (!projectDescription.trim()) {
        setError("Project description is required for suggestions");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const functions = getFunctions();
        const suggestFunction = httpsCallable<
          { projectId: string; projectDescription: string; projectTitle?: string },
          SuggestPaletteResponse
        >(functions, "suggestBrandPalette");

        const result = await suggestFunction({
          projectId,
          projectDescription,
          projectTitle,
        });

        setPalettes(result.data.palettes || []);
        setRecommendedIndex(result.data.recommended || 0);
        setBrandVoice(result.data.brandVoice || null);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate suggestions";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    palettes,
    recommendedIndex,
    brandVoice,
    suggestPalette,
    clearError,
  };
}
